import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Spinner from './Spinner';
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import PieChart from './panels/Fires/PieChart';
import '../styles/RiskComponent.scss';
import { useUserContext } from '../contexts/UserContext';
import { DataGrid } from '@mui/x-data-grid';

export default function RiskComponent(props) {
    const {
        getToken
    } = useUserContext();

    const {
        coordinates,
        user
    } = props;

    const columns = [
        {
          field: 'name',
          headerName: 'Fire Name',
          width: 150,
          resizeable: false
        },
        {
          field: 'date',
          headerName: 'Date',
          width: 130,
          resizeable: false
        },
        {
          field: 'area',
          headerName: 'Area (acres)',
          width: 120,
          resizeable: false
        },
        {
           field: 'agency',
           headerName: 'Agency',
           width: 120,
           resizeable: false,
           flex: 1,
        },
    ];

    const responsive = {
        superLargeDesktop: {
          breakpoint: { max: 4000, min: 3000 },
          items: 1
        },
        desktop: {
          breakpoint: { max: 3000, min: 1024 },
          items: 1
        },
        tablet: {
          breakpoint: { max: 1024, min: 464 },
          items: 1
        },
        mobile: {
          breakpoint: { max: 464, min: 0 },
          items: 1
        }
    };

    const [fireData, setFireData] = useState(null);
    const [graphData, setGraphData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (fireData && fireData.fireRiskData) {
            setGraphData([
            {
                name: 'Overall Risk',
                value: (fireData.fireRiskData.risk50 && fireData.fireRiskData.risk50 !== -9999) ? fireData.fireRiskData.risk50 * 2 : null,
                description: 'Composite percentage of overall risk at a specific address. A value of 0 indicates minimal likely exposure.'
            },
            {
                name: 'Damage',
                value: (fireData.fireRiskData.damage && fireData.fireRiskData.damage !== -9999) ? fireData.fireRiskData.damage * 2 : null,
                description: 'Based on number of fires with home losses combined with ignition and damage values, at a specified address.'
            },
            {
                name: 'Burnable',
                value: (fireData.fireRiskData.mitigationGroupElements && fireData.fireRiskData.mitigationGroupElements.burnable !== -9999) ? fireData.fireRiskData.mitigationGroupElements.burnable * 2 : null,
                description: 'Based on data points representing burnable land, at a specific address.'
            },
            {
                name: 'Severity',
                value: (fireData.fireRiskData.severity && fireData.fireRiskData.severity !== -9999) ? fireData.fireRiskData.severity * 2 : null,
                description: 'Based on intensity levels, crown fire within 1/2 mile of the fireshed, wind speed, and relative magnitude of embers being cast.'
            },
            {
                name: 'Mitigation',
                value: (fireData.fireRiskData.mitigation && fireData.fireRiskData.mitigation !== -9999) ? fireData.fireRiskData.mitigation * 2 : null,
                description: 'Based on the distance to the nearest fire station, proximity to water sources, terrain position, and the amount of burnable vegetation.'
            },
            {
                name: 'Intensity',
                value: (fireData.fireRiskData.severityGroupElements && fireData.fireRiskData.severityGroupElements.intensity !== -9999) ? fireData.fireRiskData.severityGroupElements.intensity * 2 : null,
                description: 'Based on predicted fireline intensity.'
            },
            {
                name: 'Near Water',
                value: (fireData.fireRiskData.mitigationGroupElements && fireData.fireRiskData.mitigationGroupElements.waterDist !== -9999) ? fireData.fireRiskData.mitigationGroupElements.waterDist * 2 : null,
                description: 'Based on the distance to the nearest available source of water that could be used during suppression efforts.'
            },
            {
                name: 'Near Power',
                value: (fireData.fireRiskData.frequencyGroupElements && fireData.fireRiskData.frequencyGroupElements.lineDist !== -9999) ? fireData.fireRiskData.frequencyGroupElements.lineDist * 2 : null,
                description: 'Based on the distance to the nearest electrical transmission line.'
            },
        ])
        }
    }, [fireData])

    useEffect(() => {
        if (user) {
            setLoading(true);
            getToken()
            .then((token) => {
                axios.get(`https://firewatch-api.space-eyes.com/fires/risk?lat=${coordinates[1]}&long=${coordinates[0]}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }).then((response) => {
                        setLoading(false);
                        setFireData({
                            fireRiskData: response.data.fireRiskData,
                            fireHistoryData: response.data.fireHistoryData
                        });
                }).catch((err) => {
                    console.log('error fetching fire risk', err.message);
                    setLoading(false);
                    setFireData({ error: true });
                })
            })
            .catch((error) => {
                console.error('Failed to get token:', error);
                setLoading(false);
                setFireData({ error: true });
            });
        }
    }, [coordinates]);

    const FireHistoryTable = () => {
        return (
            fireData && fireData.fireHistoryData && fireData.fireHistoryData.events && fireData.fireHistoryData.events.event ?
                <div className='risk-component-table'>
                    <DataGrid
                        rows={fireData.fireHistoryData.events.event.map((history, index) => ({
                            id: index,
                            name: history.fireName ? history.fireName : 'N/A',
                            date: history.fireStartDate ? history.fireStartDate : 'N/A',
                            area: (history.area.value && history.area.unit) ? `${parseFloat(history.area.value.toLocaleString()).toFixed(2)}` : 'N/A',
                            agency: history.agency ? history.agency : 'N/A'
                        }))}
                        columns={columns}
                        sx={{
                            boxShadow: 'none',
                            fontFamily: "Avenir Next Regular",
                            border: 'none',
                            color: 'white',
                            borderColor: 'white',
                            '& .MuiDataGrid-scrollbarFiller': {
                                backgroundColor: 'rgb(59, 59, 59)'
                            },
                            '& .MuiCheckbox-root' : {
                                color: 'white',
                                filter: 'invert(100%)'
                            },
                            '& .MuiDataGrid-columnHeader' : {
                                color: 'white',
                                backgroundColor: 'rgb(59, 59, 59)',
                            },
                            '& .MuiDataGrid-footerContainer' : {
                                backgroundColor: 'rgb(59, 59, 59)'
                            },
                            '& .MuiTablePagination-displayedRows' : {
                                color: 'white'
                            },
                            '& .MuiTablePagination-root' : {
                                color: 'white'
                            },
                            '& .MuiSvgIcon-root' : {
                                filter: 'invert(100%)'
                            },
                            '& .Mui-disabled' : {
                                filter: 'invert(100%)'
                            },
                            '& .MuiTablePagination-actions' : {
                                filter: 'invert(100%)'
                            },
                            '& .MuiDataGrid-columnSeparator': {
                                filter: 'invert(100%)'
                            }
                        }}
                        initialState={{
                        pagination: {
                            paginationModel: {
                            pageSize: 10,
                            },
                        },
                        }}
                        pageSizeOptions={[5]}
                        checkboxSelection={false}
                        disableRowSelectionOnClick
                        />
                </div>
                :
                <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    padding: '2rem',
                    textAlign: 'center',
                    color: '#666'
                }}>
                    <div style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                        No fire history found
                    </div>
                    <div style={{ fontSize: '0.9rem' }}>
                        No historical fires recorded for this location
                    </div>
                </div>
        )
    }

    RiskComponent.FireHistoryTable = FireHistoryTable;

    useEffect(() => {
        console.log(fireData)
    }, [fireData])

    return (
        loading ?
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <Spinner />
        </div>
        :
        fireData?.error ? 
        <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            padding: '2rem',
            textAlign: 'center',
            color: '#666'
        }}>
            <div style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                Unable to load risk data
            </div>
            <div style={{ fontSize: '0.9rem' }}>
                There was an error fetching fire risk information for this location
            </div>
        </div>
        :
        !graphData ? 
        <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            padding: '2rem',
            textAlign: 'center',
            color: '#666'
        }}>
            <div style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                No fire risk data available
            </div>
            <div style={{ fontSize: '0.9rem' }}>
                No risk assessment data found for this location
            </div>
        </div>
        :
        graphData &&
        <div className='risk-component'>
            {graphData && graphData.length === 0 ? (
                <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    padding: '2rem',
                    textAlign: 'center',
                    color: '#666'
                }}>
                    <div style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                        No fire risk data available
                    </div>
                    <div style={{ fontSize: '0.9rem' }}>
                        No risk assessment data found for this location
                    </div>
                </div>
            ) : graphData && (
                    <div className='risk-component-vertical-container'>
                        {graphData.map((data, index) => (
                            <div 
                                key={index} 
                                className='risk-component-container'
                            >
                                <div className='risk-component-container-pie'>
                                    <PieChart 
                                        percentage={data.value}
                                        size={90}
                                        type='risk'
                                    />
                                    <div className='risk-component-container-pie-caption'>{data.name}</div>
                                </div>
                                <div className='risk-component-container-description'>{data.description}</div>

                            </div>
                        ))}
                    </div>
            )}
        </div>

    )
}